import React, { useState } from "react";
import {
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Grid,
  TablePagination,
  CircularProgress,
  Alert,
  Theme,
  IconButton,
  Menu,
  MenuItem as MuiMenuItem,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useAppointments } from "@/hooks/useAppointments";
import {
  AppointmentFilter,
  AppointmentFilterOption,
  Appointment,
  AppointmentStatusEnum,
} from "@/domain/appointment.interface";
import AppointmentItem from "./AppointmentItem";
import AppointmentModal from "@/ui/modals/appointments/AppointmentModal";
import Button from "@mui/material/Button";

interface AppointmentsCardProps {
  participantId: string;
  enrollmentId: string;
  programId: string;
  theme: Theme;
}

const FILTER_OPTIONS: AppointmentFilterOption[] = [
  { key: "all", value: "All" },
  { key: "last30", value: "Last 30 days" },
  { key: "next30", value: "Next 30 days" },
];

const AppointmentsCard: React.FC<AppointmentsCardProps> = ({
  participantId,
  enrollmentId,
  programId,
  theme,
}) => {
  const [filter, setFilter] = useState<AppointmentFilter>("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedAppointment, setSelectedAppointment] =
    useState<Appointment | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuAppointment, setMenuAppointment] = useState<Appointment | null>(
    null
  );

  const {
    appointments,
    totalCount,
    isLoadingAppointments,
    isFetchingAppointments,
    appointmentsError,
    refetchAppointments,
  } = useAppointments({
    participantId,
    pageNumber: page,
    pageSize: rowsPerPage,
    sortField: "startDate",
    // Newest first. Ascending put the oldest appointment on page 1, so a coordinator who had just
    // booked one had to page to the END of the list to see it — the record they were most likely
    // to be looking for was the hardest to reach, and it got worse with every appointment added.
    sortOrder: "desc",
    filter,
  });

  const handleOpenCreate = () => {
    setSelectedAppointment(null);
    setModalMode("create");
    setModalOpen(true);
  };

  const handleOpenEdit = (appointment: Appointment, target?: HTMLElement) => {
    setSelectedAppointment(appointment);
    setModalMode("edit");
    setModalOpen(true);
    if (target) {
      setAnchorEl(target);
      setMenuAppointment(appointment);
    }
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    appointment: Appointment
  ) => {
    if (appointment.status !== AppointmentStatusEnum.SCHEDULED) return;
    setAnchorEl(event.currentTarget);
    setMenuAppointment(appointment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuAppointment(null);
  };

  const handleFilterChange = (event: SelectChangeEvent<AppointmentFilter>) => {
    setFilter(event.target.value as AppointmentFilter);
    setPage(0); // Reset to first page when filter changes
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Show error state
  if (appointmentsError) {
    return (
      <Grid
        container
        mb={2.5}
        sx={{
          boxShadow: 1,
          borderRadius: 2,
          bgcolor: "background.paper",
        }}
      >
        <Grid item container spacing={1} xs sm={12} padding={3}>
          <Grid item xs={12}>
            <Alert severity="error">
              Failed to load appointments. Please try again later.
            </Alert>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      mb={2.5}
      sx={{
        boxShadow: 1,
        borderRadius: 2,
        bgcolor: "background.paper",
      }}
    >
      <Grid
        item
        container
        spacing={1}
        xs
        sm={12}
        padding={3}
        justifyContent="flex-start"
      >
        {/* Header */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              color={theme.palette.textMain.dark2}
              variant="headlineSmall"
            >
              Appointments
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              sx={{ textTransform: "none" }}
            >
              Add
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
            <FormControl size="small" sx={{ minWidth: 230 }}>
              <InputLabel>Filter</InputLabel>
              <Select
                value={filter}
                label="Filter"
                onChange={handleFilterChange}
              >
                {FILTER_OPTIONS.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>

        {/* Loading State */}
        {isLoadingAppointments && (
          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          </Grid>
        )}

        {/* Appointments List */}
        {!isLoadingAppointments && appointments.length > 0 && (
          <Grid item xs={12}>
            <Box>
              {appointments.map((appointment) => (
                <Box key={appointment._id} sx={{ position: "relative" }}>
                  {appointment.status === AppointmentStatusEnum.SCHEDULED &&
                    !appointment.uiStatus?.includes("MISSED") && (
                      <IconButton
                        aria-label="appointment-actions"
                        onClick={(event) => handleMenuOpen(event, appointment)}
                        sx={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          zIndex: 1,
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    )}
                  <AppointmentItem appointment={appointment} theme={theme} />
                </Box>
              ))}
            </Box>
          </Grid>
        )}

        {!isLoadingAppointments &&
          appointments.length === 0 &&
          totalCount === 0 && (
            <Grid item xs={12}>
              <Box sx={{ textAlign: "center", p: 3 }}>
                <CalendarTodayIcon
                  sx={{ fontSize: 48, color: "text.disabled", mb: 2 }}
                />
                <Typography variant="body1" color="text.secondary">
                  There are no appointments yet.
                </Typography>
              </Box>
            </Grid>
          )}
        {!isLoadingAppointments &&
          appointments.length === 0 &&
          totalCount > 0 && (
            <Grid item xs={12}>
              <Box sx={{ textAlign: "center", p: 3 }}>
                <CalendarTodayIcon
                  sx={{ fontSize: 48, color: "text.disabled", mb: 2 }}
                />
                <Typography variant="body1" color="text.secondary">
                  No appointments found for the selected filter.
                </Typography>
              </Box>
            </Grid>
          )}

        {!isLoadingAppointments && appointments.length > 0 && (
          <Grid item xs={12}>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{ mt: 2 }}
            />
          </Grid>
        )}

        {isFetchingAppointments && !isLoadingAppointments && (
          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Refreshing...
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      <AppointmentModal
        open={modalOpen}
        mode={modalMode}
        participantId={participantId}
        enrollmentId={enrollmentId}
        programId={programId}
        appointment={selectedAppointment}
        onClose={() => setModalOpen(false)}
        onSaved={() => {
          setModalOpen(false);
          // Refresh list after save
          // reset page to 0 to ensure new item visible
          setPage(0);
          if (refetchAppointments) {
            refetchAppointments();
          }
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MuiMenuItem
          disabled={menuAppointment?.status !== AppointmentStatusEnum.SCHEDULED}
          onClick={() => {
            if (menuAppointment) {
              handleMenuClose();
              handleOpenEdit(menuAppointment);
            }
          }}
        >
          Edit
        </MuiMenuItem>
      </Menu>
    </Grid>
  );
};

export default AppointmentsCard;
